import {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
  RefObject,
} from 'react'
import { ThreeEvent } from '@react-three/fiber'
import { useShallow } from 'zustand/react/shallow'
import { TrackballControls as TrackballControlsImpl } from 'three-stdlib'

import { CameraControlsImpl } from '@react-three/drei'
import { CONTROLS, useCADPageStore } from '@/state'
import { useCADQuery } from '@/services/queries/cads'
import { Object3D, Object3DEventMap, Box3, Sphere, Vector3 } from 'three'

/**
 * Converts a Vector3 position to azimuth and polar angles
 * @param {Object} vector - Vector3 object with x, y, z properties
 * @returns {Object} Object containing azimuth and polar angles in radians
 */
const vectorToSphericalFromFront = (vector: Vector3) => {
  // Normalize the vector
  const length = Math.sqrt(
    vector.x * vector.x + vector.y * vector.y + vector.z * vector.z,
  )
  const normalizedVector = {
    x: vector.x / length,
    y: vector.y / length,
    z: vector.z / length,
  }

  // Calculate standard spherical coordinates
  let polar = Math.acos(normalizedVector.y)
  let azimuth = Math.atan2(normalizedVector.x, normalizedVector.z)

  // Ensure azimuth is in [-π, π] range for shortest rotation
  if (azimuth > Math.PI) {
    azimuth -= 2 * Math.PI
  } else if (azimuth < -Math.PI) {
    azimuth += 2 * Math.PI
  }

  // Front face is at (0,0,1) with azimuth=0, polar=π/2
  if (Math.abs(normalizedVector.x) > 0.9) {
    // Left/Right face
    polar = Math.PI / 2 // Horizontal rotation
    azimuth = normalizedVector.x > 0 ? Math.PI / 2 : -Math.PI / 2
  } else if (Math.abs(normalizedVector.y) > 0.9) {
    // Top/Bottom face
    azimuth = 0 // No horizontal rotation
    polar = normalizedVector.y > 0 ? 0 : Math.PI
  } else if (normalizedVector.z < -0.9) {
    polar = Math.PI / 2
    azimuth = Math.PI // Rotate 180 degrees horizontally
  }

  return { azimuth, polar }
}

interface useOrthographicViewsProps {
  cameraControlsRef: RefObject<CameraControlsImpl | null>
  trackballControlsRef: RefObject<TrackballControlsImpl | null>
}

export const useOrthographicViews = ({
  cameraControlsRef,
  trackballControlsRef,
}: useOrthographicViewsProps) => {
  const {
    data: { gltf },
  } = useCADQuery()
  const rotating = useRef(false)
  const setState = useCADPageStore(useShallow((state) => state.setState))
  const controlsId = useCADPageStore(useShallow((state) => state.controlsId))
  const sceneCenter = useCADPageStore(useShallow((state) => state.sceneCenter))
  const [rotationDirection, setRotationDirection] = useState<Vector3 | null>(
    null,
  )
  const model = useMemo(() => {
    return gltf?.scene.children[0]
  }, [gltf])

  const setRotationTo = useCallback(
    (e: ThreeEvent<MouseEvent>, direction: Vector3) => {
      e.stopPropagation()

      if (rotating.current) return null
      rotating.current = true

      setRotationDirection(direction)
      setState({ controlsId: CONTROLS.CAMERA_CONTROLS, fitting: true })
      return null
    },
    [setState],
  )

  const rotateAndFitTo = useCallback(
    (
      model: Object3D<Object3DEventMap>,
      azimuth: number,
      polar: number,
      options?: { animate: boolean },
    ) => {
      const animate = options?.animate ?? false
      const boundingBox = new Box3().setFromObject(model)
      const sphere = boundingBox.getBoundingSphere(new Sphere())
      const oldPosition = trackballControlsRef.current?.object.position.clone()

      if (cameraControlsRef.current && sceneCenter && oldPosition) {
        // Determine desired camera up based on target polar angle
        // Top (polar ≈ 0) / Bottom (polar ≈ π): use Z-up to keep screen-up stable in plan views
        const isTop = Math.abs(polar) < 1e-4
        const isBottom = Math.abs(polar - Math.PI) < 1e-4

        // Always set camera controls to default Y-up
        cameraControlsRef.current.camera.up.set(0, 1, 0)
        cameraControlsRef.current.updateCameraUp()
        trackballControlsRef.current?.target.copy(sceneCenter)

        if (cameraControlsRef.current) {
          Promise.all([
            cameraControlsRef.current.rotateTo(azimuth, polar, true),
            cameraControlsRef.current.fitToSphere(sphere, animate),
          ])
            .catch((error) => {
              console.error('Camera operations failed:', error)
            })
            .finally(() => {
              rotating.current = false
              setRotationDirection(null)
              if (isTop) {
                cameraControlsRef.current?.camera.up.set(0, 0, -1)
                cameraControlsRef.current?.updateCameraUp()
              } else if (isBottom) {
                cameraControlsRef.current?.camera.up.set(0, 0, 1)
                cameraControlsRef.current?.updateCameraUp()
              }
              setState({ controlsId: CONTROLS.TRACKBALL_CONTROLS })
            })
        }
      }
    },
    [sceneCenter, setState, cameraControlsRef, trackballControlsRef],
  )

  const rotateAndFit = useCallback(() => {
    if (!model || !rotationDirection) return
    const { azimuth, polar } = vectorToSphericalFromFront(rotationDirection)
    rotateAndFitTo(model, azimuth, polar, { animate: true })
  }, [model, rotationDirection, rotateAndFitTo])

  useEffect(() => {
    if (controlsId === CONTROLS.CAMERA_CONTROLS && rotating.current) {
      rotateAndFit()
    }
  }, [controlsId, rotateAndFit])

  return { setRotationTo }
}
